import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  KeyRound, Loader2, MapPin, House, Check, Hammer, Clock, X, Phone, ArrowLeft, AlertCircle,
} from "lucide-react";
import { api, photoUrl } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { PHONE_DISPLAY, PHONE_TEL } from "@/lib/constants";

const MILESTONE_STYLE = {
  termine: { label: "Terminé", icon: Check, dot: "bg-sage text-white", line: "bg-sage/40" },
  en_cours: { label: "En cours", icon: Hammer, dot: "bg-cognac text-white", line: "bg-cognac/30" },
  a_venir: { label: "À venir", icon: Clock, dot: "bg-slate-200 text-slate-400", line: "bg-slate-200" },
};

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : null;

export default function SuiviChantier() {
  const { code: routeCode } = useParams();
  const [code, setCode] = useState(routeCode || "");
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lightbox, setLightbox] = useState(null);

  const load = useCallback(async (c) => {
    if (!c || !c.trim()) return;
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/suivi/${encodeURIComponent(c.trim())}`);
      setProject(res.data);
    } catch (e) {
      setProject(null);
      setError(e.response?.data?.detail || "Code inconnu — vérifiez le code reçu de Cozyhome");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (routeCode) load(routeCode);
  }, [routeCode, load]);

  return (
    <div data-testid="suivi-page" className="min-h-screen bg-cream">
      <header className="grain relative bg-navy text-white">
        <div className="relative z-10 mx-auto max-w-5xl px-5 pb-14 pt-7 sm:px-8">
          <div className="flex items-center justify-between">
            <Link to="/" data-testid="suivi-back-home" className="flex items-center gap-3">
              <Logo boxed className="h-7 w-auto" boxClassName="rounded-xl px-2.5 py-1.5" />
              <span className="font-display text-xl font-semibold tracking-tight">cozyhome</span>
            </Link>
            <Link
              to="/"
              className="flex items-center gap-2 text-sm font-semibold text-white/60 transition-colors duration-300 hover:text-cognac-light"
            >
              <ArrowLeft className="h-4 w-4" /> Retour au site
            </Link>
          </div>

          {!project && (
            <div className="mx-auto mt-14 max-w-md">
              <p className="text-center text-xs font-bold uppercase tracking-[0.25em] text-cognac-light">
                Espace client
              </p>
              <h1 className="mt-3 text-center font-display text-4xl font-medium tracking-tight sm:text-5xl">
                Suivi de votre chantier
              </h1>
              <p className="mt-4 text-center text-sm leading-relaxed text-white/60">
                Saisissez le code d'accès transmis par Cozyhome pour suivre
                l'avancement de votre construction en temps réel.
              </p>
              <form
                data-testid="suivi-access-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  load(code);
                }}
                className="mt-8"
              >
                <div className="flex items-center gap-2 rounded-full border border-white/20 bg-white/5 p-2 backdrop-blur-sm focus-within:border-cognac">
                  <KeyRound className="ml-3 h-5 w-5 shrink-0 text-cognac-light" />
                  <input
                    data-testid="suivi-code-input"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder="Votre code (ex. DEMO2026)"
                    className="w-full bg-transparent text-sm font-semibold tracking-widest text-white placeholder:text-white/35 focus:outline-none"
                  />
                  <button
                    data-testid="suivi-access-button"
                    type="submit"
                    disabled={loading}
                    className="flex shrink-0 items-center gap-2 rounded-full bg-cognac px-6 py-3 text-sm font-bold text-white transition-colors duration-300 hover:bg-white hover:text-navy disabled:opacity-60"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Accéder"}
                  </button>
                </div>
                {error && (
                  <p data-testid="suivi-error" className="mt-4 flex items-center justify-center gap-2 text-sm font-semibold text-red-300">
                    <AlertCircle className="h-4 w-4" /> {error}
                  </p>
                )}
              </form>
            </div>
          )}

          {project && (
            <div data-testid="suivi-dashboard" className="mt-12">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-cognac-light">
                Chantier de {project.client_name}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                {project.model && (
                  <span data-testid="suivi-model" className="flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm font-semibold">
                    <House className="h-4 w-4 text-cognac-light" /> {project.model}
                  </span>
                )}
                {project.address && (
                  <span data-testid="suivi-address" className="flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm font-semibold">
                    <MapPin className="h-4 w-4 text-cognac-light" /> {project.address}
                  </span>
                )}
              </div>
              <div className="mt-10">
                <div className="flex items-end justify-between">
                  <p className="text-sm font-semibold text-white/70">Avancement global</p>
                  <p data-testid="suivi-progress-value" className="font-display text-5xl font-medium text-cognac-light">
                    {project.progress}<span className="text-2xl">%</span>
                  </p>
                </div>
                <div className="mt-3 h-3 overflow-hidden rounded-full bg-white/15">
                  <motion.div
                    data-testid="suivi-progress-bar"
                    initial={{ width: 0 }}
                    animate={{ width: `${project.progress}%` }}
                    transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                    className="h-full rounded-full bg-cognac"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {project && (
        <main className="mx-auto max-w-5xl px-5 py-16 sm:px-8">
          <h2 className="font-display text-3xl font-medium text-navy sm:text-4xl">Les grandes étapes</h2>
          <ol className="mt-10">
            {project.milestones.map((m, i) => {
              const st = MILESTONE_STYLE[m.status] || MILESTONE_STYLE.a_venir;
              const Icon = st.icon;
              const d = fmtDate(m.date);
              return (
                <motion.li
                  key={m.id}
                  data-testid={`suivi-milestone-${i}`}
                  initial={{ opacity: 0, x: -16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: Math.min(i * 0.04, 0.3) }}
                  className="relative flex gap-5 pb-8 last:pb-0"
                >
                  {i < project.milestones.length - 1 && (
                    <span className={`absolute left-[19px] top-10 h-full w-0.5 ${st.line}`} />
                  )}
                  <span className={`z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${st.dot}`}>
                    <Icon className="h-4.5 w-4.5" />
                  </span>
                  <div className="flex-1 rounded-2xl border border-navy/10 bg-white px-5 py-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className={`font-bold ${m.status === "a_venir" ? "text-slate-400" : "text-navy"}`}>
                        {m.title}
                      </p>
                      <span
                        data-testid={`suivi-milestone-status-${i}`}
                        className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider ${
                          m.status === "termine"
                            ? "bg-sage/20 text-navy"
                            : m.status === "en_cours"
                              ? "bg-cognac/15 text-cognac-dark"
                              : "bg-slate-100 text-slate-400"
                        }`}
                      >
                        {st.label}
                      </span>
                    </div>
                    {d && <p className="mt-1 text-xs text-slate-400">{d}</p>}
                  </div>
                </motion.li>
              );
            })}
          </ol>

          {project.photos.length > 0 && (
            <section className="mt-16">
              <h2 className="font-display text-3xl font-medium text-navy sm:text-4xl">
                Photos du chantier
              </h2>
              <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {project.photos.map((ph, i) => (
                  <button
                    key={ph.id}
                    data-testid={`suivi-photo-${i}`}
                    onClick={() => setLightbox(ph)}
                    className="group overflow-hidden rounded-2xl text-left"
                  >
                    <img
                      src={photoUrl(ph)}
                      alt={ph.caption || "Photo du chantier"}
                      loading="lazy"
                      className="h-56 w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    {ph.caption && (
                      <p className="bg-white px-4 py-3 text-sm font-semibold text-navy">{ph.caption}</p>
                    )}
                  </button>
                ))}
              </div>
            </section>
          )}

          <div className="mt-16 flex flex-wrap items-center justify-between gap-6 rounded-3xl bg-navy px-8 py-8 text-white">
            <div>
              <p className="font-display text-2xl font-medium">Une question sur votre chantier ?</p>
              <p className="mt-1 text-sm text-white/60">Votre interlocuteur unique vous répond directement.</p>
            </div>
            <a
              data-testid="suivi-call-button"
              href={PHONE_TEL}
              className="flex items-center gap-2.5 rounded-full bg-cognac px-7 py-3.5 text-sm font-bold transition-colors duration-300 hover:bg-white hover:text-navy"
            >
              <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
            </a>
          </div>
        </main>
      )}

      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="suivi-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-navy/90 p-5 backdrop-blur-sm"
          >
            <button
              data-testid="suivi-lightbox-close"
              className="absolute right-5 top-5 rounded-full border border-white/25 p-2.5 text-white transition-colors duration-300 hover:bg-white hover:text-navy"
              aria-label="Fermer"
            >
              <X className="h-5 w-5" />
            </button>
            <motion.figure
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-4xl"
            >
              <img src={photoUrl(lightbox)} alt={lightbox.caption || "Photo du chantier"} className="max-h-[80vh] w-auto rounded-2xl" />
              {lightbox.caption && (
                <figcaption className="mt-4 text-center text-sm font-semibold text-white/80">{lightbox.caption}</figcaption>
              )}
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
